"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { getCookieValue, isPGS } from "@packages/lib/utlils/commonFunction";
import { handlePostSigninSideEffects } from "@packages/hooks/usePostAuth";
import RegistrationModal from "@packages/shared-components/user-profile/signin-signup/registration-modal";
import { useOnRegistrationModalClose } from "@packages/hooks/useIsRegistrationModal";
import { logClickstreamEvent } from "@packages/lib/utlils/clickstream";
import { peekOAuthSubmissionType } from "@packages/shared-components/user-profile/signin-signup/ga-tracking";
import {
  CS_EVENT_TYPES,
  CS_SIGNIN_METHODS,
  CS_FUNCTIONALITY,
  CS_ACTION_TYPE,
  CS_NETWORK_ID,
  CS_PGS_NETWORK_ID,
  CS_CTA_TITLES,
} from "@packages/lib/utlils/clickstream-constants";

const REOPEN_FLAG = "reopen-registration-modal";

/**
 * Global handler for post-OAuth social login flows.
 *
 * After a social sign-in/sign-up from a modal (fav, RI, etc.), the OAuth
 * redirect navigates away from the page. On return, this component checks
 * the `reopen-registration-modal` sessionStorage flag and decides:
 *
 * - **Sign-up** (new_user_flag cookie exists): reopen the RegistrationModal
 *   so the user sees the benefits → success screens.
 * - **Sign-in** (no new_user_flag): no modal needed — just show a toast.
 *   If a non-advert flow is active, trigger the PDF download first.
 *
 * Placed in the root layout so it works on every page, regardless of
 * whether FavFunctionalityWrapper is present.
 */
const PostAuthModalHandler = () => {
  const [showModal, setShowModal] = useState(false);
  const pathname = usePathname();

  // Listen for modal close event dispatched by Registration/LoginForm after auth completes
  useOnRegistrationModalClose(() => setShowModal(false));

  useEffect(() => {
    if (typeof window === "undefined") return;

    const shouldReopen = sessionStorage.getItem(REOPEN_FLAG);
    if (!shouldReopen) return;
  
    const isNewUser = getCookieValue("new_user_flag")?.toLowerCase() === "y";
    
    if (isNewUser) {
      // Sign-up (flag = "y"): reopen modal to show benefits → success screens
      setShowModal(true);
      sessionStorage.removeItem(REOPEN_FLAG);
    } else {
      // Sign-in (flag absent or "n"): no modal needed, handle toast + non-advert PDF if applicable
      // Clickstream: Java parity — fire SignedIn for social OAuth sign-in (modal flow)
      // Java: UserRegisterationController.registrationClickStream() with signedInMethod=Google/Apple
      const provider = peekOAuthSubmissionType();
      if (provider === "google" || provider === "apple") {
        const isGoogle = provider === "google";
        const csPageName = localStorage?.getItem("gaPageName") || "Register";
        logClickstreamEvent({
          eventType: CS_EVENT_TYPES.SIGNED_IN,
          pageName: csPageName,
          CTATitle: isGoogle ? CS_CTA_TITLES.CONTINUE_WITH_GOOGLE : CS_CTA_TITLES.CONTINUE_WITH_APPLE,
          functionalityName: CS_FUNCTIONALITY.SIGN_IN,
          actionType: CS_ACTION_TYPE,
          signedinMethod: isGoogle ? CS_SIGNIN_METHODS.GOOGLE : CS_SIGNIN_METHODS.APPLE,
          networkId: isPGS() ? CS_PGS_NETWORK_ID : CS_NETWORK_ID,
          surveyConsent: "",
          newsletterConsent: "",
          solusConsent: "",
          interestedIntakeYear: "",
          userSegmentation: "",
        });
      }
      sessionStorage.removeItem(REOPEN_FLAG);
      handlePostSigninSideEffects();
    }
  }, [pathname]);

  if (!showModal) return null;

  return <RegistrationModal onClose={() => setShowModal(false)} />;
};

export default PostAuthModalHandler;
